import { IOnInit } from "angular";
import * as _ from 'lodash';
import * as moment from "moment";
import { FeesRepo } from '@services/fees.repo'
import { PortfolioFee, SetupFee, FeeType, FeeTier } from '@models/fees/fee.models';
import { PortfolioRepo } from '@services/portfolio.repo';
import { AuthService } from '@common/auth.service';
import { Roles } from '@common/enums/Roles';
import { extractErrorMessage } from "@common/utils/errorUtils";

class CreatePortfolioFeeDialogController implements IOnInit {

  public portfolioFee: PortfolioFee;
  public feeTypes: any
  public clientPortfolios: any
  public potentialPortfoliosForFees: any
  public clientPortfolioFees: any // All fees for the client to check portfolio availability

  public portfolioFeeForm: ng.IFormController;
  public isLoading: boolean = false;
  public isLoadingFeeTypes: boolean = false;
  public isLoadingApproval: boolean = false;
  public isEditMode: boolean = false;

  /**
   * True when the portfolio fee is approved — all editable fields and Add Setup Fee are locked.
   * Pending fees are always editable (including end date), regardless of how far in the past it is.
   */
  public get isLocked(): boolean {
    return !!(this.portfolioFee && this.portfolioFee.approved);
  }

  // Setup fees management
  public setupFees: Array<SetupFee> = [];

  // Raw string bound to the fee value text input, preserving intermediate states like "0."
  // during typing. portfolioFee.value holds the parsed numeric value separately.
  public rawFeeValue: string = '';

  public onFeeValueInput = () => {
    if (!this.portfolioFee) return;
    const v = this.rawFeeValue;
    if (v === '' || v === null || v === undefined) {
      this.portfolioFee.value = null;
    } else {
      // Support comma as decimal separator (e.g. "0,0075" for European locales)
      const str = v.replace(',', '.');
      const num = parseFloat(str);
      this.portfolioFee.value = isNaN(num) ? null : num;
    }
  }

  /** @ngInject */
  constructor(
    private $mdDialog: angular.material.IDialogService, 
    private notifyjs, 
    private feesRepo: FeesRepo,
    private portfolioRepo: PortfolioRepo,
    private $scope: angular.IScope,
    private $timeout: angular.ITimeoutService,
    private authService: AuthService
  ) {

  }

  $onInit() {
    // Check if we're in edit mode - use != null to handle both null and undefined
    this.isEditMode = !!(this.portfolioFee && this.portfolioFee.id != null && this.portfolioFee.id !== undefined);

    // Pre-initialise so the fee-type slide-out form is always in the DOM (just hidden)
    this.newFeeType = this.emptyFeeType();
    
    // Load fee types if not provided
    if (!this.feeTypes || this.feeTypes.length === 0) {
      this.isLoadingFeeTypes = true;
      this.isLoading = true; // Show modal loader while loading fee types
      this.feesRepo.getFeeTypes(true)
        .then((res) => {
          this.feeTypes = res.data;
          this.isLoadingFeeTypes = false;
          this.isLoading = false;
          // After fee types are loaded, initialize the portfolio fee
          this.initializePortfolioFee();
        })
        .catch((err) => {
          this.isLoadingFeeTypes = false;
          this.isLoading = false;
          this.notifyjs("Failed to load fee types: " + extractErrorMessage(err));
        });
    } else {
      // Fee types already provided, initialize immediately
      this.initializePortfolioFee();
    }
    
    // Set up date validation watcher
    this.setupDateValidation();
    
    // Set up portfolios validation watcher
    this.setupPortfoliosValidation();
  }

  private initializePortfolioFee = () => {
    if (this.portfolioFee) {
      // Find and set fee type
      if (this.portfolioFee.feeType && this.feeTypes) {
        const foundFeeType = this.feeTypes.find((feeType) => {
          return feeType.id == this.portfolioFee.feeType.id;
        });
        if (foundFeeType) {
          this.portfolioFee.feeType = foundFeeType;
        }
      }
      
      // Convert fee value from decimal (0-1) to percentage (0-100) for display
      // Backend stores as decimal, but form displays as percentage
      if (this.portfolioFee.value != null && this.portfolioFee.value <= 1) {
        this.portfolioFee.value = this.portfolioFee.value * 100;
      }
      this.rawFeeValue = this.portfolioFee.value != null ? String(this.portfolioFee.value) : '';
      
      // Convert dates from ISO string to YYYY-MM-DD for date-picker component
      if (this.portfolioFee.fromDate) {
        this.portfolioFee.fromDate = this.portfolioFee.fromDate.split('T')[0];
      }
      
      if (this.portfolioFee.toDate) {
        this.portfolioFee.toDate = this.portfolioFee.toDate.split('T')[0];
      }

      // Ensure comment exists
      if (!this.portfolioFee.comment) {
        this.portfolioFee.comment = '';
      }
      
      // Ensure portfolios array exists
      if (!this.portfolioFee.portfolios) {
        this.portfolioFee.portfolios = [];
      }
      
      // Initialize setup fees array
      if (this.portfolioFee.setupFees && this.portfolioFee.setupFees.length > 0) {
        this.setupFees = this.portfolioFee.setupFees.map((sf: SetupFee) => ({
          ...sf,
          chargeMonth: sf.chargeMonth ? sf.chargeMonth.split('T')[0] : null
        }));
      } else {
        this.setupFees = [];
      }
    } else {
      // New portfolio fee - no default value so the fee value field starts empty
      this.portfolioFee = {
        portfolios: [],
        feeType: null,
        value: null,
        fromDate: null,
        toDate: null,
        comment: '',
        approved: false,
        setupFees: []
      } as any;
      this.setupFees = [];
      this.rawFeeValue = '';
    }
    
    // Use clientPortfolios for the multiselect (all portfolios will be shown, disabled ones will be disabled)
    if (!this.clientPortfolios && this.potentialPortfoliosForFees) {
      this.clientPortfolios = this.potentialPortfoliosForFees;
    }
    
    // Match portfolio objects by portfolioId so md-select can properly show selected items
    // This is needed because md-select compares objects by reference, not by value
    if (this.portfolioFee && this.portfolioFee.portfolios && this.portfolioFee.portfolios.length > 0 && this.clientPortfolios && this.clientPortfolios.length > 0) {
      this.portfolioFee.portfolios = this.portfolioFee.portfolios.map((selectedPortfolio: any) => {
        if (!selectedPortfolio) return null;
        
        // Get the ID from the selected portfolio (could be portfolioId or id)
        const selectedId = selectedPortfolio.portfolioId || selectedPortfolio.id;
        if (!selectedId) return selectedPortfolio; // Can't match without an ID
        
        // Find the matching portfolio object from clientPortfolios by ID
        const matchedPortfolio = this.clientPortfolios.find((p: any) => {
          if (!p) return false;
          const portfolioId = p.portfolioId || p.id;
          return portfolioId === selectedId;
        });
        
        return matchedPortfolio || selectedPortfolio;
      }).filter((p: any) => p != null); // Remove any that couldn't be matched
    }
    
    // Trigger form validation after initialization to ensure pre-populated values are recognized
    // This is especially important in edit mode where values are set programmatically
    // Use $timeout to ensure DOM is rendered and form fields are initialized
    this.$timeout(() => {
      if (this.portfolioFeeForm) {
        // Manually trigger validation on all form fields that exist
        Object.keys(this.portfolioFeeForm).forEach((fieldName) => {
          const field = this.portfolioFeeForm[fieldName];
          // Only validate actual form fields (not form controller properties like $valid, $invalid, etc.)
          if (field && typeof field === 'object' && field.$validate && !fieldName.startsWith('$')) {
            field.$validate();
          }
        });
        
        // Also trigger validation on the date fields specifically
        if (this.portfolioFeeForm.fromDate && this.portfolioFee.fromDate) {
          this.portfolioFeeForm.fromDate.$validate();
        }
        if (this.portfolioFeeForm.toDate && this.portfolioFee.toDate) {
          this.portfolioFeeForm.toDate.$validate();
        }
      }
    }, 100); // Small delay to ensure date-picker components are initialized
  } // end of initializePortfolioFee

  cancel = () => {
    this.$mdDialog.hide();
  }

  // Check if a portfolio is disabled: only exclude portfolios that have an active portfolio fee
  // (active = fee has no end date). All others are in the list.
  isPortfolioDisabled = (portfolio: any): boolean => {
    if (!this.clientPortfolioFees || !portfolio) {
      return false;
    }
    return this.clientPortfolioFees.some((otherFee: PortfolioFee) => {
      if (this.isEditMode && this.portfolioFee && otherFee.id === this.portfolioFee.id) {
        return false;
      }
      const portfolioInOtherFee = otherFee.portfolios && otherFee.portfolios.some((p: any) =>
        (p.portfolioId || p.id) === (portfolio.portfolioId || portfolio.id)
      );
      if (!portfolioInOtherFee) {
        return false;
      }
      // Only disable if the other fee is active (end date is empty)
      const otherTo = otherFee.toDate;
      return otherTo === null || otherTo === undefined || otherTo === '';
    });
  }

  // Custom validity check so the Update button is not blocked by dynamic setup-fee controls
  // (md-select/date-picker in ng-repeat can stay $invalid due to timing)
  isFormValid = (): boolean => {
    if (!this.portfolioFeeForm || !this.portfolioFee) {
      return false;
    }
    const form = this.portfolioFeeForm;
    // Main form fields (only check controls that exist)
    if (form.feeTypeName && !form.feeTypeName.$valid) return false;
    if (form.portfolios && !form.portfolios.$valid) return false;
    if (form.fromDate && !form.fromDate.$valid) return false;
    if (form.toDate && (!form.toDate.$valid || (form.toDate.$error && (form.toDate.$error.dateRange || form.toDate.$error.dateOverlap)))) return false;
    if (this.portfolioFee.feeType && !this.portfolioFee.feeType.isTiered && form.feeValue && !form.feeValue.$valid) return false;
    if (this.isEditMode && form.comment && !form.comment.$valid) return false;
    // Setup fees: validate data directly (don't rely on dynamic row controls)
    if (this.setupFees && this.setupFees.length > 0) {
      for (const setupFee of this.setupFees) {
        if (!setupFee.portfolioId || !setupFee.amount || setupFee.amount <= 0 || !setupFee.chargeMonth) {
          return false;
        }
      }
    }
    return true;
  }

  save = () => {
    // Validate portfolios before saving
    if (!this.portfolioFee.portfolios || this.portfolioFee.portfolios.length === 0) {
      this.notifyjs("Please select at least one portfolio.");
      return;
    }

    // Validate dates before saving
    if (!this.validateDates()) {
      this.notifyjs("Start date must be earlier than end date and they cannot be on the same day.");
      return;
    }

    if (this.checkDateOverlapWithExistingFees()) {
      this.notifyjs("The selected date range overlaps with an existing portfolio fee for one or more selected portfolios.");
      return;
    }

    // Validate comment for edit mode
    if (this.isEditMode && (!this.portfolioFee.comment || this.portfolioFee.comment.length < 5)) {
      this.notifyjs("Comment must be at least 5 characters long.");
      return;
    }

    // Fee value is required and must be greater than 0 when fee type is not tiered
    if (this.portfolioFee.feeType && !this.portfolioFee.feeType.isTiered) {
      const val = this.portfolioFee.value;
      if (val == null || val === undefined) {
        this.notifyjs("Please enter a fee value.");
        return;
      }
      const numVal = typeof val === 'number' ? val : parseFloat(String(val));
      if (isNaN(numVal) || numVal <= 0) {
        this.notifyjs("Fee value must be greater than 0.");
        return;
      }
      if (numVal > 100) {
        this.notifyjs("Fee value must be 100% or less.");
        return;
      }
    }

    // Validate setup fees - all fields must be filled
    if (this.setupFees && this.setupFees.length > 0) {
      for (let i = 0; i < this.setupFees.length; i++) {
        const setupFee = this.setupFees[i];
        if (!setupFee.portfolioId) {
          this.notifyjs(`Setup fee ${i + 1}: Portfolio is required.`);
          return;
        }
        if (!setupFee.amount || setupFee.amount <= 0) {
          this.notifyjs(`Setup fee ${i + 1}: Amount (MUR) is required and must be greater than 0.`);
          return;
        }
        if (!setupFee.chargeMonth) {
          this.notifyjs(`Setup fee ${i + 1}: Month is required.`);
          return;
        }
      }
      // Ensure no setup fee is for a portfolio that already has a setup fee on another portfolio fee card
      const portfolioIdsWithSetupFeeElsewhere = this.getPortfolioIdsWithSetupFeeOnOtherCard();
      const duplicate = this.setupFees.find((sf: SetupFee) => portfolioIdsWithSetupFeeElsewhere.includes(sf.portfolioId));
      if (duplicate) {
        this.notifyjs("One or more selected portfolios already have a setup fee on another portfolio fee. A setup fee can only be added once per portfolio.");
        return;
      }
    }

    this.isLoading = true;

    // Convert fee value from percentage to decimal if not tiered
    // Note: feeValue is stored as percentage (0-100) in portfolioFee.value for display,
    // but needs to be converted to decimal (0-1) for backend
    // Always divide by 100 because the form value is always in percentage format (e.g. 75% = 75, 0.75% = 0.75)
    if (!this.portfolioFee.feeType.isTiered && this.portfolioFee.value != null) {
      this.portfolioFee.value = this.portfolioFee.value / 100;
    } else if (this.portfolioFee.feeType.isTiered) {
      this.portfolioFee.value = 0;
    }

    // Dates are already in YYYY-MM-DD format from the date-picker, backend handles conversion

    // Set setup fees array - convert to the format expected by backend
    if (this.setupFees && this.setupFees.length > 0) {
      this.portfolioFee.setupFees = this.setupFees.map((sf: SetupFee) => ({
        ...sf,
        chargeMonth: sf.chargeMonth || null
      }));
    } else {
      this.portfolioFee.setupFees = [];
    }

    if (this.isEditMode) {
      // Edit existing fee
      this.feesRepo.editClientFee(this.portfolioFee)
        .then((res) => {
          this.isLoading = false;
          this.$mdDialog.hide(true);
        })
        .catch((err) => {
          this.isLoading = false;
          this.notifyjs("Could not update the fee : " + extractErrorMessage(err));
        });
    } else {
      // Create new fee
      this.feesRepo.savePortfolioFees(this.portfolioFee)
        .then((res) => {
          this.isLoading = false;
          this.$mdDialog.hide(true);
        })
        .catch((err) => {
          this.isLoading = false;
          this.notifyjs("Could not save the fee for the selected portfolios : " + extractErrorMessage(err));
        });
    }
  } // end of save

  getPortfolioAccountNumber = (portfolio) => {
    if (!portfolio) return { accountNumber: '' };
    // Handle different portfolio structures
    if (portfolio.accounts && portfolio.accounts.length > 0) {
      return _.first(portfolio.accounts);
    }
    // Fallback: return portfolio name or accountNumber if available
    return {
      accountNumber: portfolio.accountNumber || portfolio.name || ''
    };
  }

  validateDates = () => {
    if (this.portfolioFee.fromDate && this.portfolioFee.toDate) {
      // Simple string comparison works for YYYY-MM-DD format
      if (this.portfolioFee.fromDate >= this.portfolioFee.toDate) {
        return false;
      }
      // Check if dates are on the same day
      if (this.portfolioFee.fromDate === this.portfolioFee.toDate) {
        return false;
      }
    }
    return true;
  }

  // Check if the current portfolio + date selection overlaps with any existing fee; set date validity.
  // Only an issue when the same portfolio(s) are in both: we flag overlap only when an existing fee
  // shares at least one portfolio with the selection and date ranges overlap. Otherwise dates can overlap.
  checkDateOverlapWithExistingFees = (): boolean => {
    const form = this.portfolioFeeForm;
    if (!form || !form.fromDate || !form.toDate || !this.clientPortfolioFees || !this.portfolioFee) {
      return false;
    }
    const portfolios = this.portfolioFee.portfolios;
    if (!portfolios || portfolios.length === 0) {
      form.fromDate.$setValidity('dateOverlap', true);
      form.toDate.$setValidity('dateOverlap', true);
      return false;
    }
    const newFrom = this.portfolioFee.fromDate
      ? (typeof this.portfolioFee.fromDate === 'string' ? this.portfolioFee.fromDate : moment(this.portfolioFee.fromDate).format('YYYY-MM-DD'))
      : null;
    const newTo = this.portfolioFee.toDate
      ? (typeof this.portfolioFee.toDate === 'string' ? this.portfolioFee.toDate : moment(this.portfolioFee.toDate).format('YYYY-MM-DD'))
      : null;
    if (!newFrom) {
      form.fromDate.$setValidity('dateOverlap', true);
      form.toDate.$setValidity('dateOverlap', true);
      return false;
    }
    const selectedIds = portfolios.map((p: any) => p.portfolioId || p.id);
    const newEnd = newTo || '9999-12-31';
    const hasOverlap = this.clientPortfolioFees.some((otherFee: PortfolioFee) => {
      if (this.isEditMode && this.portfolioFee && otherFee.id === this.portfolioFee.id) {
        return false;
      }
      const otherFrom = otherFee.fromDate
        ? (typeof otherFee.fromDate === 'string' ? otherFee.fromDate : moment(otherFee.fromDate).format('YYYY-MM-DD'))
        : null;
      const otherTo = otherFee.toDate
        ? (typeof otherFee.toDate === 'string' ? otherFee.toDate : moment(otherFee.toDate).format('YYYY-MM-DD'))
        : null;
      const otherStart = otherFrom || '0000-01-01';
      const otherEnd = otherTo || '9999-12-31';
      const overlapsRange = otherStart <= newEnd && otherEnd >= newFrom;
      if (!overlapsRange) return false;
      // Only flag when the same portfolio(s) are in this existing fee
      const otherPortfolioIds = (otherFee.portfolios || []).map((p: any) => p.portfolioId || p.id);
      return selectedIds.some((id: number) => otherPortfolioIds.includes(id));
    });
    form.fromDate.$setValidity('dateOverlap', !hasOverlap);
    form.toDate.$setValidity('dateOverlap', !hasOverlap);
    return hasOverlap;
  }

  // Set up date validation for the form
  setupDateValidation = () => {
    // Use $timeout to ensure form is initialized
    this.$timeout(() => {
      if (this.portfolioFeeForm && this.portfolioFeeForm.toDate) {
        const runValidation = () => {
          if (!this.portfolioFeeForm || !this.portfolioFeeForm.toDate) return;
          if (this.portfolioFee.fromDate && this.portfolioFee.toDate && !this.validateDates()) {
            this.portfolioFeeForm.toDate.$setValidity('dateRange', false);
          } else {
            this.portfolioFeeForm.toDate.$setValidity('dateRange', true);
          }
          this.checkDateOverlapWithExistingFees();
        };
        this.$scope.$watch(() => [this.portfolioFee.fromDate, this.portfolioFee.toDate], runValidation, true);
        this.$scope.$watch(() => (this.portfolioFee.portfolios || []).length, runValidation);
      }
    }, 0);
  }

  // Set up portfolios validation for the form
  setupPortfoliosValidation = () => {
    // Use $timeout to ensure form is initialized
    this.$timeout(() => {
      if (this.portfolioFeeForm && this.portfolioFeeForm.portfolios) {
        // Watch for changes to validate portfolios
        this.$scope.$watch(() => this.portfolioFee.portfolios, () => {
          if (this.portfolioFeeForm && this.portfolioFeeForm.portfolios) {
            // Manually set validity based on portfolios validation
            if (!this.portfolioFee.portfolios || this.portfolioFee.portfolios.length === 0) {
              this.portfolioFeeForm.portfolios.$setValidity('required', false);
            } else {
              this.portfolioFeeForm.portfolios.$setValidity('required', true);
            }
          }
        }, true);
      }
    }, 0);
  }


  // Portfolio IDs that already have a setup fee on another portfolio fee card (not this one).
  // Used to restrict the setup-fee portfolio dropdown so a setup fee can only be added once per portfolio.
  getPortfolioIdsWithSetupFeeOnOtherCard = (): number[] => {
    if (!this.clientPortfolioFees || this.clientPortfolioFees.length === 0) {
      return [];
    }
    const currentFeeId = this.isEditMode && this.portfolioFee ? this.portfolioFee.id : null;
    const ids: number[] = [];
    this.clientPortfolioFees.forEach((fee: PortfolioFee) => {
      if (fee.id === currentFeeId) return;
      if (fee.setupFees && fee.setupFees.length > 0) {
        fee.setupFees.forEach((sf: SetupFee) => {
          if (sf.portfolioId != null && ids.indexOf(sf.portfolioId) === -1) ids.push(sf.portfolioId);
        });
      }
    });
    return ids;
  }

  // Get available portfolios for setup fees (portfolios not yet used in this dialog and not already having a setup fee on another card)
  getAvailablePortfoliosForSetupFee = (): Array<any> => {
    if (!this.portfolioFee || !this.portfolioFee.portfolios) {
      return [];
    }
    const usedInThisDialog = this.setupFees.map((sf: SetupFee) => sf.portfolioId);
    const usedOnOtherCard = this.getPortfolioIdsWithSetupFeeOnOtherCard();
    return this.portfolioFee.portfolios.filter((p: any) => {
      const portfolioId = p.portfolioId || p.id;
      return !usedInThisDialog.includes(portfolioId) && !usedOnOtherCard.includes(portfolioId);
    });
  }

  // Get available portfolios for a specific setup fee row (excludes portfolios used in other rows and those with setup fee on another card)
  getAvailablePortfoliosForSetupFeeRow = (currentIndex: number): Array<any> => {
    if (!this.portfolioFee || !this.portfolioFee.portfolios) {
      return [];
    }
    const usedOnOtherCard = this.getPortfolioIdsWithSetupFeeOnOtherCard();
    const usedInOtherRows = this.setupFees
      .filter((sf: SetupFee, index: number) => index !== currentIndex)
      .map((sf: SetupFee) => sf.portfolioId);
    const currentRowPortfolioId = this.setupFees[currentIndex] && this.setupFees[currentIndex].portfolioId;

    return this.portfolioFee.portfolios.filter((p: any) => {
      const portfolioId = p.portfolioId || p.id;
      if (usedOnOtherCard.includes(portfolioId)) return false;
      return !usedInOtherRows.includes(portfolioId) || currentRowPortfolioId === portfolioId;
    });
  }

  // Handle portfolio change for a setup fee row - load default cash account info
  onSetupFeePortfolioChange = (index: number) => {
    this.loadCashAccountForSetupFee(index);
  }

  // Check if add button should be disabled (locked when fee is approved)
  canAddSetupFee = (): boolean => {
    if (this.isLocked) return false;
    return this.getAvailablePortfoliosForSetupFee().length > 0;
  }


  addSetupFee = () => {
    const availablePortfolios = this.getAvailablePortfoliosForSetupFee();
    if (availablePortfolios.length === 0) {
      this.notifyjs("No available portfolios for setup fee");
      return;
    }
    
    const firstAvailablePortfolio = availablePortfolios[0];
    const portfolioId = firstAvailablePortfolio.portfolioId || firstAvailablePortfolio.id;
    
    // Determine default month: use portfolio fee start date if set, otherwise use current month
    let chargeMonth: string;
    if (this.portfolioFee && this.portfolioFee.fromDate) {
      // Use the start date's month/year
      const startDate = new Date(this.portfolioFee.fromDate);
      const year = startDate.getFullYear();
      const month = startDate.getMonth() + 1;
      const monthStr = month < 10 ? '0' + month : String(month);
      chargeMonth = `${year}-${monthStr}-01`;
    } else {
      // Default to current month
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const monthStr = month < 10 ? '0' + month : String(month);
      chargeMonth = `${year}-${monthStr}-01`;
    }
    
    const newSetupFee: SetupFee = {
      portfolioId: portfolioId,
      amount: 10000, // Default amount in MUR
      chargeMonth: chargeMonth,
      canEdit: true
    };
    
    this.setupFees.push(newSetupFee);
    
    // Load cash account info for the newly added setup fee
    this.loadCashAccountForSetupFee(this.setupFees.length - 1);
  }
  
  // Load default cash account info for a setup fee from available portfolio data
  loadCashAccountForSetupFee = (index: number) => {
    const setupFee = this.setupFees[index];
    if (!setupFee || !setupFee.portfolioId) {
      if (setupFee) {
        setupFee.cashAccountNumber = null;
        setupFee.cashAccountCurrency = null;
        setupFee.cashAccountDescription = null;
      }
      return;
    }
    
    // Get cash account info from the portfolio data (from backend)
    // First try from potentialPortfoliosForFees (which should have cash account info from backend)
    const portfolioWithCashAccount = (this.potentialPortfoliosForFees || []).find((p: any) => {
      const pid = p.portfolioId || p.id;
      return pid === setupFee.portfolioId;
    });
    
    // If not found, try from clientPortfolios
    const portfolio = portfolioWithCashAccount || (this.clientPortfolios || []).find((p: any) => {
      const pid = p.portfolioId || p.id;
      return pid === setupFee.portfolioId;
    });
    
    if (portfolio) {
      // Use cash account info from portfolio (populated by backend)
      setupFee.cashAccountNumber = portfolio.defaultCashAccountNumber || null;
      setupFee.cashAccountCurrency = portfolio.defaultCashAccountCurrency || null;
      setupFee.cashAccountDescription = portfolio.defaultCashAccountDescription || null;
    } else {
      // Portfolio not found in available data
      setupFee.cashAccountNumber = null;
      setupFee.cashAccountCurrency = null;
      setupFee.cashAccountDescription = null;
    }
  }

  removeSetupFee = (index: number) => {
    if (index >= 0 && index < this.setupFees.length) {
      if (this.isLocked) {
        this.notifyjs("Portfolio fee is approved; changes are locked.");
        return;
      }
      const setupFee = this.setupFees[index];
      if (setupFee.canEdit === false) {
        this.notifyjs("This setup fee has been finalized and cannot be removed.");
        return;
      }
      this.setupFees.splice(index, 1);
    }
  }

  // Approval status methods - Senior Management or System Administrator can approve/unapprove (maker-checker)
  public hasFeeApprovalRole = (): boolean => {
    const userRoles = this.authService.getUserRoles();
    if (!userRoles) return false;
    return (
      userRoles.includes(Roles.SeniorManagement) ||
      userRoles.includes(Roles.SystemAdministrator)
    );
  };

  public getUserApprovalStatus = (): { canApprove: boolean; message: string } => {
    // Only allow approval in edit mode
    if (!this.isEditMode) {
      return { canApprove: false, message: "Approval can only be changed when editing an existing fee" };
    }

    if (!this.hasFeeApprovalRole()) {
      return { canApprove: false, message: "Only a senior manager or system administrator can approve a fee" };
    }
    
    // Prevent self-approval - block both creator and last editor (maker-checker)
    if (this.portfolioFee && (this.portfolioFee as any).isAddedByCurrentUser) {
      return { canApprove: false, message: 'A fee cannot be approved by the user who created it.' };
    }
    if (this.portfolioFee && (this.portfolioFee as any).isLastEditedByCurrentUser) {
      return { canApprove: false, message: 'A fee cannot be approved by the user who last edited it.' };
    }
    
    return { canApprove: true, message: 'approved' };
  };

  /** Same rule as API: end date strictly before one calendar month ago (UTC). */
  private isPortfolioFeeLockedByEndDate(): boolean {
    if (!this.portfolioFee || !this.portfolioFee.toDate) {
      return false;
    }
    const endDate = moment.utc(this.portfolioFee.toDate).startOf('day');
    const todayUtc = moment.utc().startOf('day');
    const oneMonthAgo = todayUtc.clone().subtract(1, 'month');
    console.log("end date", endDate.isBefore(oneMonthAgo));
    return endDate.isBefore(oneMonthAgo);
  }

  /** Approved fee with end date far enough in the past that it cannot be edited (matches API lock). */
  public isApprovedFeeLockedByEndDate = (): boolean => {
    return !!(this.portfolioFee && this.portfolioFee.approved && this.isPortfolioFeeLockedByEndDate());
  };

  /** Unapproval uses same roles as fee update. */
  public getUserUnapprovalStatus = (): { canUnapprove: boolean; message: string } => {
    if (!this.isEditMode) {
      return { canUnapprove: false, message: "Approval can only be changed when editing an existing fee" };
    }
    if (!this.hasFeeApprovalRole()) {
      return { canUnapprove: false, message: "Only a senior manager or system administrator can unapprove a fee" };
    }
    if (this.isPortfolioFeeLockedByEndDate()) {
      return { canUnapprove: false, message: '' };
    }
    return { canUnapprove: true, message: "" };
  };

  public onApproveClick = (): void => {
    const status = this.getUserApprovalStatus();
    if (!status.canApprove) {
      this.notifyjs(status.message, "error");
      return;
    }
    this.isLoadingApproval = true;
    this.feesRepo.approvePortfolioFee(this.portfolioFee.id)
      .then(() => {
        this.isLoadingApproval = false;
        this.$mdDialog.hide(true);
      })
      .catch((err) => {
        this.isLoadingApproval = false;
        this.notifyjs("Could not approve fee: " + extractErrorMessage(err));
      });
  };

  public onUnapproveClick = (): void => {
    const status = this.getUserUnapprovalStatus();
    if (!status.canUnapprove) {
      this.notifyjs(status.message, "error");
      return;
    }
    this.portfolioFee.approved = false;
  };

  public getAddedByName = (): string => {
    if (!this.portfolioFee || !this.portfolioFee.addedBy) return '';
    // Handle both cases: addedBy as string (name) or as User object
    return typeof this.portfolioFee.addedBy === 'string' 
      ? this.portfolioFee.addedBy 
      : (this.portfolioFee.addedBy as any).name || '';
  };

  public getModifiedByName = (): string => {
    if (!this.portfolioFee || !this.portfolioFee.modifiedBy) return '';
    // Handle both cases: modifiedBy as string (name) or as User object
    return typeof this.portfolioFee.modifiedBy === 'string' 
      ? this.portfolioFee.modifiedBy 
      : (this.portfolioFee.modifiedBy as any).name || '';
  };

  public convertToLocaleTime = (date: any): string => {
    if (!date) return '';
    return moment(new Date(date)).format("YYYY-MM-DD");
  };

  // ─── Add Fee Type slide-out panel ────────────────────────────────────────

  public isFeeTypePanelOpen: boolean = false;
  public isFeeTypePanelFormValid: boolean = false;
  public isSavingFeeType: boolean = false;
  public newFeeTypePanelError: string = '';
  public newFeeType: FeeType = null;

  private emptyFeeType(): FeeType {
    return {
      id: 0,
      name: '',
      description: '',
      isTiered: false,
      isActive: true,
      feeTiers: [],
    } as any;
  }

  public onFeeTypePanelValidChange = (isValid: boolean): void => {
    this.isFeeTypePanelFormValid = isValid;
  };

  public openFeeTypePanel = (): void => {
    this.newFeeType = this.emptyFeeType();
    this.isFeeTypePanelFormValid = false;
    this.newFeeTypePanelError = '';
    this.isFeeTypePanelOpen = true;
    document.body.classList.add('slide-out-panel-open');
  };

  public closeFeeTypePanel = (): void => {
    this.isFeeTypePanelOpen = false;
    document.body.classList.remove('slide-out-panel-open');
  };

  public saveFeeType = (): void => {
    if (!this.newFeeType || !this.newFeeType.name || !this.newFeeType.description) {
      this.newFeeTypePanelError = 'Please fill in all required fields.';
      return;
    }

    const tiersPayload: FeeTier[] = this.newFeeType.isTiered
      ? (this.newFeeType.feeTiers || []).map(ft => ({
          id: -1,
          low: ft.low,
          high: ft.high == null ? 0 : ft.high,
          value: ft.value / 100,
        }))
      : [];

    const payload: FeeType = _.cloneDeep(this.newFeeType);
    payload.feeTiers = tiersPayload;

    this.isSavingFeeType = true;
    this.newFeeTypePanelError = '';

    this.feesRepo.saveFeeType(payload)
      .then(() => this.feesRepo.getFeeTypes(true))
      .then((res: any) => {
        this.isSavingFeeType = false;
        this.feeTypes = res.data;
        const savedName = this.newFeeType.name;
        const created = (this.feeTypes as FeeType[]).find(ft => ft.name === savedName);
        if (created) {
          this.portfolioFee.feeType = created;
        }
        this.closeFeeTypePanel();
      })
      .catch((err: any) => {
        this.isSavingFeeType = false;
        this.newFeeTypePanelError = extractErrorMessage(err) || 'Failed to save fee type.';
      });
  };

}

export const createPortfolioFeeDialog: angular.IComponentOptions = {
  template: require('./createPortfolioFee.dialog.html'),
  controller: CreatePortfolioFeeDialogController,
  bindings: {
    portfolioFee: "<",
    feeTypes: "<?", // Optional: if not provided, will be loaded internally
    clientPortfolios: "<",
    potentialPortfoliosForFees: "<",
    clientPortfolioFees: "<" // Optional: all fees to check portfolio availability
  }

}



// WEBPACK FOOTER //
// ./src/app/fees/clientFeeManagement/createPortfolioFeeDialog/createPortfolioFee.dialog.ts